/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Fragment } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import {
  Accordion,
  AccordionHeader,
  AccordionPanel,
  Divider,
  Grid,
  Hidden,
  Link,
  Typography,
  Typography2,
  useTheme,
} from '@coursera/cds-core';
import type { LinkProps } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import SkillSetCard from 'bundles/enterprise-learner-search/components/skillsetSearch/SkillSetCard';
import type { SkillSetSearchResults } from 'bundles/enterprise-learner-search/components/skillsetSearch/SkillSetSearchDataProvider';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/program-common';

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps>(Link);

// We need the router as a property because Aloglia discards the context on SSR, including router
type OtherInputProps = {
  showDivider?: boolean;
  programHomeLink: string;
};

type Props = {
  skillsetHits: SkillSetSearchResults[];
} & OtherInputProps;

const useStyles = () => {
  const theme = useTheme();
  return {
    banner: css`
      margin-bottom: ${theme.spacing(8)};
    `,
    title: css`
      display: flex;
      align-items: center;
    `,
    pill: css`
      background: ${theme.palette.purple[800]};
      border-radius: 4px;
      color: ${theme.palette.white};
      padding: ${theme.spacing(4, 8)};
      margin-left: ${theme.spacing(12)};
    `,
    description: css`
      display: flex;
      align-items: center;
      gap: ${theme.spacing(8)};
      margin-top: 4px;
    `,
    leftPanel: css`
      ${theme.breakpoints.down('sm')} {
        margin: ${theme.spacing(12, 24)};
      }
    `,
    rightPanel: css`
      margin-top: ${theme.spacing(48)};
      margin-bottom: ${theme.spacing(32)};
      border-left: 1px solid ${theme.palette.gray[300]};
      padding-left: ${theme.spacing(24)};
      padding-bottom: ${theme.spacing(16)};
      gap: ${theme.spacing(16)};

      ${theme.breakpoints.down('sm')} {
        margin-top: ${theme.spacing(4)};
        margin-bottom: ${theme.spacing(4)};
        padding-right: ${theme.spacing(24)};
      }
    `,
    divider: css`
      margin: ${theme.spacing(12, 0)};
    `,
  };
};

const SkillSetSearchResultBanner = ({ skillsetHits, programHomeLink, showDivider }: Props) => {
  const styles = useStyles();

  const whySkillSets = [
    _t('Course suggestions tailored to your skill level'),
    _t('Track your skill progress'),
    _t('Short assessments to check your skill levels'),
    _t('Help to bridge skill gaps and deepen your knowledge'),
  ];

  if (skillsetHits.length === 0 || skillsetHits[0].hits.length === 0) {
    return null;
  }

  const matches = skillsetHits[0].hits.slice(0, 2);

  const trackingData = {
    skillsetMatches: matches.map((match) => ({
      name: match.name,
      objectID: match.objectID,
      topSkill: match.topSkill,
    })),
  };

  return (
    <Fragment>
      <Grid css={styles.banner} container>
        <Grid css={styles.leftPanel} item md={5} sm={12}>
          <TrackedDiv
            css={styles.title}
            withVisibilityTracking
            trackingName="skillset_search_results"
            data={trackingData}
          >
            <Typography variant="h2semibold" component="h2">
              {_t('Find your next course with a SkillSet role')}
            </Typography>

            <Typography2 variant="bodyPrimary" color="invertBody" component="span" css={styles.pill}>
              {_t('New')}
            </Typography2>
          </TrackedDiv>
          <Typography2 component="p" variant="bodyPrimary">
            <FormattedMessage
              message={_t('Top {numberOfMatches, plural, =1 {match} other {matches}} for your search')}
              numberOfMatches={matches.length}
            />
          </Typography2>
          {matches.map((s) => (
            <SkillSetCard key={s.objectID} skillset={s} />
          ))}
          <TrackedLink
            {...Link.defaultProps}
            trackingName="view_all_skillsets"
            trackingData={trackingData}
            href={`${programHomeLink}?currentTab=SKILLSETS`}
            tabIndex={0}
          >
            {_t('Explore all available SkillSets')}
          </TrackedLink>
        </Grid>
        <Grid css={styles.rightPanel} item md={5} sm={12}>
          <Hidden smDown>
            <Typography2 variant="subtitleMedium" component="h3">
              {_t('Why learn with SkillSets?')}
            </Typography2>
            {whySkillSets.map((reason) => (
              <div css={styles.description} key={reason}>
                <CheckIcon size="small" />
                <Typography2 component="p" variant="bodyPrimary">
                  {reason}
                </Typography2>
              </div>
            ))}
          </Hidden>
          <Hidden mdUp>
            <Accordion>
              <AccordionHeader label={_t('Why learn with SkillSets?')} />
              <AccordionPanel>
                {whySkillSets.map((reason) => (
                  <div css={styles.description} key={reason}>
                    <CheckIcon size="small" />
                    <Typography2 component="p" variant="bodyPrimary">
                      {reason}
                    </Typography2>
                  </div>
                ))}
              </AccordionPanel>
            </Accordion>
          </Hidden>
        </Grid>
      </Grid>
      {showDivider && <Divider css={styles.divider} />}
    </Fragment>
  );
};

export default SkillSetSearchResultBanner;
